import React from 'react';
import {
  isEmpty,
  toLower,
  filter,
  orderBy,
  map,
  pick,
  find,
  flatten,
  groupBy
} from 'lodash';
import { ColumnHeader, Spacing } from './styled';
import { TM_FRAUD_SOURCE_IDS } from '../../../util/GlobalConstant';

export const getGlobalSettings = ({ settings, gsQuery }) => {
  if (!settings || !gsQuery) {
    console.error('Error: settings or gsQuery is empty.');
    return [];
  }

  const { uiWidgetGroups } = settings;

  if (!uiWidgetGroups || !Array.isArray(uiWidgetGroups)) {
    console.error('Error: uiWidgetGroups is not an array or is empty.');
    return [];
  }

  const { widgetGroupId, widgetId } = gsQuery;

  if (!widgetGroupId || !widgetId) {
    console.error('Error: gsQuery is missing widgetGroupId or widgetId.');
    return [];
  }

  const widgetGroup = uiWidgetGroups.find(
    (g) => g.widgetGroupId === widgetGroupId
  );

  if (!widgetGroup) {
    console.error(
      'Error: No widget group found with the specified widgetGroupId.'
    );
    return [];
  }

  const widgetConfig = widgetGroup.widgets.find((w) => w.widgetId === widgetId);

  if (!widgetConfig) {
    console.error('Error: No widget found with the specified widgetId.');
    return [];
  }

  return widgetConfig;
};

export const filterOrder = ({ filterKey, orderKey, data }) =>
  orderBy(filter(data, filterKey), [orderKey], ['asc']);

export const genarateRendererConfigs = ({
  data,
  settings,
  configs,
  doFlatten = false
}) => {
  if (isEmpty(data) || isEmpty(settings) || isEmpty(configs)) return [];

  const visibilitySettings = filterOrder({
    filterKey: 'columnVisibility',
    orderKey: 'columnIndex',
    data: settings
  });

  const result = map(visibilitySettings, ({ columnDetail }) => {
    const visibleItems = filterOrder({
      filterKey: 'visibility',
      orderKey: 'itemIndex',
      data: columnDetail
    });

    return map(visibleItems, (item) => {
      const { itemName } = item;
      const { customComponents, defaultComponent } = configs;
      const componentProps =
        find(customComponents, ['itemName', itemName]) || defaultComponent;

      const {
        gsPick = ['itemLabel', 'itemName'],
        addtionalPick = [],
        component = null
      } = componentProps;

      return {
        ...pick(item, gsPick),
        ...pick(data, addtionalPick),
        component,
        itemValue: (() => {
          let itemValue;
          if (itemName === 'alertType') {
            if (data['alertSourceId'] === TM_FRAUD_SOURCE_IDS.TM_PROD_ID) {
              itemValue = 'AML-TM Detection';
            } else if (
              data['alertSourceId'] === TM_FRAUD_SOURCE_IDS.FRAUD_PROD_ID
            ) {
              itemValue = 'Fraud Detection';
            } else {
              itemValue = '';
            }
          } else if (
            itemName === 'adverseNewsFlag' ||
            itemName === 'specialAttentionFlag'
          ) {
            let hasField = false;

            // have to check if field is given
            Object.keys(data).forEach((datakey) => {
              if (datakey === itemName) {
                hasField = true;
              }
            });

            // if confirmed have field
            if (hasField) {
              itemValue = data[itemName] ? 'Yes' : 'No';
            }
          } else {
            itemValue = data[itemName];
          }

          return itemValue;
        })()
      };
    });
  });

  return doFlatten ? flatten(result) : result;
};

export const genarateTableConfigs = ({ settings, configs }) => {
  if (isEmpty(settings)) return [];

  const visibilitySettings = filterOrder({
    filterKey: 'columnVisibility',
    orderKey: 'columnIndex',
    data: settings
  });

  const result = map(visibilitySettings, ({ columnDetail }) => {
    const visibleItems = filterOrder({
      filterKey: 'visibility',
      orderKey: 'itemIndex',
      data: columnDetail
    });

    const { customProperties, defaultProperties } = configs;

    return map(visibleItems, (item) => {
      const { itemLabel, itemName } = item;
      const renderer = (value, record) => {
        if (
          record[itemName] !== undefined ||
          record[itemName] !== null ||
          itemName == 'beneficiarySummary' ||
          itemName == 'isSentToCm' ||
          itemName === 'originatorBeneficiaryName'
        ) {
          return (
            <React.Fragment key={itemName}>
              {customProperties[itemName]?.render(
                record[itemName] || '-',
                record
              ) || defaultProperties.render(record[itemName] || '-', record)}
            </React.Fragment>
          );
        } else {
          return '-';
        }
      };

      return {
        title: itemLabel,
        dataIndex: itemName,
        key: itemName,
        align: customProperties[itemName]?.align || 'Left',
        render: (value, record) => <> {renderer(value, record)}</>
      };
    });
  });

  return flatten(result);
};

export const generateGroupedTableConfigs = ({ settings, configs }) => {
  if (isEmpty(settings)) return [];

  const visibilitySettings = filterOrder({
    filterKey: 'columnVisibility',
    orderKey: 'columnIndex',
    data: settings
  });

  const groupedSettings = groupBy(visibilitySettings, 'columnName');

  const { customProperties, defaultProperties } = configs;

  const result = Object.keys(groupedSettings).map((key) => ({
    title: <ColumnHeader>{key}</ColumnHeader>,
    children: groupedSettings[key].reduce((accum, { columnDetail }) => {
      const visibleItems = filterOrder({
        filterKey: 'visibility',
        orderKey: 'itemIndex',
        data: columnDetail
      });

      const title = (
        <>
          {visibleItems.map(({ itemName, itemLabel }, index) => (
            <React.Fragment key={itemName}>
              <div>{itemLabel}</div>
              {index < visibleItems.length - 1 && <Spacing />}
            </React.Fragment>
          ))}
        </>
      );

      const renderer = (value, record) => (
        <div>
          {visibleItems.map(({ itemName }, index) => (
            <React.Fragment key={itemName}>
              <div>
                {customProperties[itemName]?.render(
                  record[itemName] || '-',
                  record
                ) || defaultProperties.render(record[itemName] || '-', record)}
              </div>
              {index < visibleItems.length - 1 && <Spacing />}
            </React.Fragment>
          ))}
        </div>
      );

      return [
        ...accum,
        {
          title,
          render: (value, record) => <>{renderer(value, record)}</>
        }
      ];
    }, [])
  }));

  return flatten(result);
};

export const generateCsvConfigs = ({ settings }) => {
  if (isEmpty(settings)) return [];

  const visibilitySettings = filterOrder({
    filterKey: 'columnVisibility',
    orderKey: 'columnIndex',
    data: settings
  });

  const result = map(visibilitySettings, ({ columnDetail }) => {
    const visibleItems = filterOrder({
      filterKey: 'visibility',
      orderKey: 'itemIndex',
      data: columnDetail
    });

    return map(visibleItems, (item) => {
      const { itemLabel, itemName } = item;

      let customItemLabel;
      switch (itemName) {
        case 'originator':
          customItemLabel = 'Originator Name';
          break;

        case 'originatorBankCountryCode':
          customItemLabel = 'Originator Country';
          break;

        case 'beneficiary':
          customItemLabel = 'Beneficiary Name';
          break;

        case 'beneficiaryBankCountryCode':
          customItemLabel = 'Beneficiary Country';
          break;

        case 'transactionCurrencyAmount':
          customItemLabel = 'Transaction Amount';
          break;

        case 'bankInfo':
          customItemLabel = 'Bank Information';
          break;

        case 'trancheNo':
          customItemLabel = 'Tranche No.';
          break;

        case 'transactionPurpose':
          customItemLabel = 'Transaction Purpose';
          break;

        case 'atmId':
          customItemLabel = 'ATM ID';
          break;

        case 'tellerId':
          customItemLabel = 'Teller ID';
          break;

        case 'oppAccountNumber':
          customItemLabel = 'Opp Account Number';
          break;

        case 'bankBranchCode':
          customItemLabel = 'Bank Branch Code';
          break;

        case 'transactionTypeCode':
          customItemLabel = 'Transaction Type Code';
          break;

        case 'originatorBeneficiaryName':
          customItemLabel = 'Originator Bank Name\n Beneficiary Bank Name';
          break;

        case 'sourceOfTxnFunds':
          customItemLabel = 'Source Of Funds';
          break;

        case 'counterpartyNationality':
          customItemLabel = 'Counterparty Nationality';
          break;

        case 'counterpartyType':
          customItemLabel = 'Counterparty Type';
          break;

        case 'thirdPartyRefund':
          customItemLabel = 'Third Party Refund';
          break;

        case 'ipRiskiness':
          customItemLabel = 'IP Riskiness';
          break;

        default:
      }

      return {
        label: customItemLabel || itemLabel,
        name: itemName,
        key: itemName
      };
    });
  });

  return flatten(result);
};

export const generateCsvData = (data) => {
  return data.map((item) => ({
    ...item,
    isCredit:
      item.isCredit == null || item.isCredit == undefined
        ? null
        : item.isCredit
        ? 'Credit'
        : 'Debit',
    originatorBeneficiaryName: `${item.originatorBankName || ''} - ${
      item.beneficialBankName || ''
    }`,
    beneficiaryC2cLinkagePresent: item.beneficiaryC2cLinkagePresent
      ? 'Yes'
      : 'No',
    beneficiaryAdverseNews: item.beneficiaryAdverseNews ? 'Yes' : 'No',
    transactionDate: item.transactionDate
  }));
};

export const getAlertColorCode = (level) => {
  if (isEmpty(level)) {
    return '';
  }

  const colorCodes = {
    high: '#DF6057',
    medium: '#E29840',
    good: '#E29840',
    low: '#71C969'
  };

  return colorCodes[toLower(level)] || '#000';
};

const getAccess = (authenticationData, resourceCode, context) => {
  let access = false;
  authenticationData.forEach((item) => {
    if (item?.details?.resource?.resourceCode == resourceCode) {
      access = item?.details?.resource?.operations?.includes(context);
    }
  });
  return access;
};

export const checkAccess = (
  authentication = [],
  resourceCode = '',
  context = ''
) => {
  const { data = [] } = authentication;
  return getAccess(data, resourceCode, context);
};

export const setCookie = (name, value, days) => {
  var expires = '';
  if (days) {
    var date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = '; expires=' + date.toUTCString();
  }
  document.cookie = name + '=' + (value || '') + expires + '; Secure; path=/';
};

export const getCookie = (name) => {
  var nameEQ = name + '=';
  var ca = document.cookie.split(';');
  for (var i = 0; i < ca.length; i++) {
    var c = ca[i];
    while (c.charAt(0) == ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

export const eraseCookie = (name) => {
  document.cookie =
    name +
    '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT; Secure; SameSite=Lax;';
};

export const getCategoryLabel = (keyName) => {
  switch (keyName?.toLowerCase()) {
    case 'customer_segment_name':
      return 'Business Segment';
    case 'risk_level':
      return 'Risk Level';
    case 'entity_type':
      return 'Customer Type';
    case 'citizenship_country':
    case 'residence_operation_country':
      return 'Geography';
    case 'nature_of_business':
    case 'business_type':
      return 'Occupation/Nature of Business';
    default:
      return keyName;
  }
};

export const isNullOrEmpty = (value) => {
  return value === null || value === undefined || value === '';
};

export const btoaUTF8 = (str) => {
  const bytes = new TextEncoder().encode(str); // Encode to Uint8Array
  let binary = '';

  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]); // Convert to binary string
  }

  return btoa(binary); // Encode to Base64
};
